import { FileText, Paperclip } from "lucide-react";
import { useTranslate } from "ra-core";
import { useRef } from "react";

import { Button } from "@/components/ui/button";

import { DeleteIconButton } from "./DeleteIconButton";
import { SectionTitle } from "./SectionTitle";

export interface DocumentItem {
  key: string | number;
  title: string;
  src?: string;
}

export interface DocumentsSectionProps {
  items: DocumentItem[];
  onAttach: (files: File[]) => Promise<void> | void;
  onDelete: (key: DocumentItem["key"]) => Promise<void> | void;
  isUploading?: boolean;
}

export const DocumentsSection = ({
  items,
  onAttach,
  onDelete,
  isUploading = false,
}: DocumentsSectionProps) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle icon={<Paperclip />}>
        {translate("crm.operations.documents.title")}
      </SectionTitle>
      {items.map((item) => (
        <div
          key={item.key}
          className="flex items-center gap-2 rounded-md border px-3 py-1.5"
        >
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          {item.src ? (
            <a
              href={item.src}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-sm underline-offset-2 hover:underline"
            >
              {item.title}
            </a>
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm">
              {item.title}
            </span>
          )}
          <DeleteIconButton
            label={translate("crm.operations.documents.delete")}
            onConfirm={() => onDelete(item.key)}
          />
        </div>
      ))}
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        data-testid="document-input"
        onChange={async (event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (files.length > 0) await onAttach(files);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip />
        {translate("crm.operations.documents.attach")}
      </Button>
    </section>
  );
};
